import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChickRearMethod } from '../../utils/enums';
import { UpdateChickDto, ChickInfo } from '../../api/chicks';
import CageSelect from '../CageSelect';

interface EditChickFormProps {
  chick: ChickInfo;
  onSubmit: (data: UpdateChickDto) => void;
  onCancel: () => void;
}

export default function EditChickForm({ chick, onSubmit, onCancel }: EditChickFormProps) {
  const [formData, setFormData] = useState<UpdateChickDto>({
    hatchDate: chick.hatchDate.toISOString().split('T')[0],
    rearMethod: chick.rearMethod,
    cageNumber: chick.cageNumber,
    notes: chick.notes || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.hatchDate) {
      newErrors.hatchDate = 'Hatch date is required';
    }

    if (!formData.cageNumber) {
      newErrors.cageNumber = 'Cage number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error updating chick:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof UpdateChickDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-900">Edit Chick Record</h3>
        <button
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="hatchDate" className="block text-sm font-medium text-slate-700">
            Hatch Date *
          </label>
          <input
            type="date"
            id="hatchDate"
            value={formData.hatchDate}
            onChange={(e) => handleInputChange('hatchDate', e.target.value)}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
              errors.hatchDate ? 'border-red-300' : ''
            }`}
          />
          {errors.hatchDate && <p className="mt-1 text-sm text-red-600">{errors.hatchDate}</p>}
        </div>

        <div>
          <label htmlFor="rearMethod" className="block text-sm font-medium text-slate-700">
            Rear Method
          </label>
          <select
            id="rearMethod"
            value={formData.rearMethod}
            onChange={(e) => handleInputChange('rearMethod', e.target.value as ChickRearMethod)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            {Object.values(ChickRearMethod).map(method => (
              <option key={method} value={method}>
                {method.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
            Cage Number *
          </label>
          <CageSelect
            id="cageNumber"
            value={formData.cageNumber || ''}
            onChange={(cageNumber) => handleInputChange('cageNumber', cageNumber)}
            required
            error={!!errors.cageNumber}
            placeholder="Search or add a cage number…"
          />
          {errors.cageNumber && <p className="mt-1 text-sm text-red-600">{errors.cageNumber}</p>}
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
            Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            value={formData.notes || ''}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="Enter any additional notes..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Chick'}
          </button>
        </div>
      </form>
    </div>
  );
} 